// src/pages/HardSkills.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/HardSkills.css";
import Navbar from "../components/Navbar"; 

// Configuración local
const PAGE_SIZE = 5;
const LS_KEY = (userId) => `hardSkillsProgress:${userId}`;

const HardSkills = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [userData, setUserData] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();

  // Guardar progreso en localStorage
  const persistLocal = (draft = {}) => {
    if (!userData?._id) return;
    const payload = {
      responses: draft.responses ?? responses,
      currentStep: draft.currentStep ?? currentStep,
      updatedAt: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(LS_KEY(userData._id), JSON.stringify(payload));
  };

  // Obtener usuario y cargar preguntas
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setUserData(response.data);
      } catch (error) {
        console.error("Error al obtener datos del usuario:", error);
      }
    };

    fetchUserData();

    setQuestions([
      "Si me preguntan cómo llegar a una ubicación, prefiero hacer un croquis que explicarlo verbalmente.",
      "Cuando me siento molesto(a) o feliz, sé exactamente el motivo.",
      "Canto, toco o alguna vez he tocado algún instrumento musical.",
      "La música me ayuda a identificar mis emociones.",
      "Tengo facilidad para hacer cálculos aritméticos de forma mental y con rapidez.",
      "Soy capaz de ayudar a mis compañeros(as) a manejar sus emociones, porque he vivido situaciones similares.",
      "Prefiero realizar operaciones matemáticas empleando la calculadora que mentalmente.",
      "Se me facilita aprender nuevos pasos de baile.",
      "Expreso lo que pienso con facilidad cuando platico con mis compañeros(as).",
      "Gozo cuando participo en una buena conversación.",
      "Tengo buen sentido de orientación espacial (norte, sur, este, oeste).",
      "Disfruto reunir a mis amigos(as) en fiestas y eventos.",
      "Necesito escuchar música para sentirme bien.",
      "Se me facilita entender instructivos y diagramas.",
      "Me gustan los juegos de destreza, crucigramas y videojuegos.",
      "Se me facilita aprender actividades físicas que requieren destreza como andar en patineta, bicicleta, etc.",
      "Me molesto cuando mis compañeros(as) dicen palabras sin sentido.",
      "Tengo capacidad de persuadir a mis compañeros(as) para que sigan mis ideas.",
      "Se me facilitan los bailes que requieren de coordinación y equilibrio.",
      "Soy hábil para identificar secuencias numéricas (1,2,4,8, __,32).",
      "Se me facilita realizar trabajos de construcción como maquetas, esculturas, etc.",
      "Tengo talento para identificar el significado de las palabras.",
      "Tengo facilidad de girar mentalmente un objeto.",
      "Hay canciones o música que me recuerdan acontecimientos importantes de mi vida.",
      "Me gusta trabajar con cálculos, números y figuras geométricas.",
      "Disfruto del silencio porque me permite meditar sobre cómo me siento.",
      "Mirar construcciones nuevas me da una sensación de bienestar.",
      "Cuando estoy relajado(a) me gusta cantar o tocar algún instrumento.",
      "Soy hábil en los deportes de rendimiento.",
      "Me gusta tomar nota en mis clases.",
      "Regularmente identifico las señales y expresiones de mi rostro.",
      "Al observar el rostro de mis compañeros(as), fácilmente identifico su estado de ánimo.",
      "Me resulta fácil identificar mis sentimientos y emociones.",
      "Reconozco fácilmente los estados de ánimo de mis compañeros(as).",
      "Me doy cuenta fácilmente de lo que piensan mis compañeros(as) de mí."
    ]);
  }, []);

  // Cargar progreso guardado
  useEffect(() => {
    if (!userData?._id) return;
    try {
      const raw = localStorage.getItem(LS_KEY(userData._id));
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.responses) setResponses(saved.responses);
        if (Number.isInteger(saved?.currentStep)) setCurrentStep(saved.currentStep);
      }
    } catch (e) {
      console.warn("No se pudo cargar el progreso local:", e);
    }
  }, [userData?._id]);

  const steps = Math.ceil(questions.length / PAGE_SIZE);

  const handleChange = (e, index) => {
    const next = { ...responses, [index]: e.target.value };
    setResponses(next);
    persistLocal({ responses: next });
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prevStep) => {
        const next = prevStep + 1;
        persistLocal({ currentStep: next });
        return next;
      });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrev = () => {
    setCurrentStep((prevStep) => {
      const next = Math.max(prevStep - 1, 0);
      persistLocal({ currentStep: next });
      return next;
    });
  };

  const validateStep = () => {
    const start = currentStep * PAGE_SIZE;
    const end = Math.min(start + PAGE_SIZE, questions.length);
    for (let i = start; i < end; i++) {
      if (!responses[i]) {
        alert("Por favor, responde todas las preguntas antes de continuar.");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/submit-hard-skills`,
        { responses },
        { headers: { Authorization: token } }
      );
  
      if (response.status === 200) {
        alert("Encuesta enviada con éxito");
        if (userData?._id) localStorage.removeItem(LS_KEY(userData._id));
        navigate("/dashboard");
      } else {
        alert("Hubo un problema al enviar la encuesta");
      }
    } catch (error) {
      console.error("Error al enviar la encuesta:", error);
      alert("Ocurrió un error. Intenta de nuevo.");
    }
  };

  const toggleSidebar = () => setShowSidebar(!showSidebar);
  const closeSidebar = () => setShowSidebar(false);

  const handleSaveAndExit = () => {
    persistLocal();
    alert("Progreso guardado localmente. Podrás continuar luego desde este equipo.");
    navigate("/dashboard");
  };

  return (
    <>
      <Navbar />

      <div className="dashboard-layout">
        <div className={`sidebar-overlay ${showSidebar ? "show" : ""}`} onClick={closeSidebar}></div>

        <div className={`dashboard-sidebar ${showSidebar ? "show" : ""}`}>
          <div className="sidebar-header">
            <h3>Inteligencias Múltiples</h3>
          </div>
          
          <div className="sidebar-menu">
            <div className="survey-progress">
              <div className="progress-info">
                <span>Progreso</span>
                <span>{currentStep + 1}/{steps}</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((currentStep + 1) / steps) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <button 
            className="menu-item return-dashboard"
            onClick={() => {
              if(window.confirm("¿Estás seguro de que deseas volver al dashboard? Tus respuestas se han guardado y podrás continuar luego.")) {
                handleSaveAndExit();
              }
            }}
          >
            <i className="bi bi-arrow-left-circle"></i>
            Volver al Dashboard
          </button>
        </div>

        <div className="dashboard-main">
          <div className="survey-container">
            <div className="survey-header">
              <h2>Evaluación de Inteligencias Múltiples</h2>
              <p className="survey-description">
                Esta evaluación nos ayudará a identificar tus tipos de inteligencia predominantes.
              </p>
              <div className="button-container">
                <button 
                  type="button" 
                  className="nav-button prev"
                  onClick={handleSaveAndExit}
                >
                  <i className="bi bi-floppy"></i>
                  Guardar y salir
                </button>
              </div>
            </div>

            <form className="survey-form" onSubmit={handleSubmit}>
              <div className="questions-group">
                {questions.slice(currentStep * PAGE_SIZE, (currentStep + 1) * PAGE_SIZE).map((question, index) => (
                  <div className="question-card" key={index + currentStep * PAGE_SIZE}>
                    <div className="question-header">
                      <span className="question-number">Pregunta {index + 1 + currentStep * PAGE_SIZE}</span>
                      <p className="question-text">{question}</p>
                    </div>
                    
                    <div className="options-grid">
                      {[
                        { value: "1", label: "Falso" },
                        { value: "5", label: "Verdadero" }
                      ].map((option) => (
                        <label 
                          key={option.value}
                          className={`option-card ${responses[index + currentStep * PAGE_SIZE] === option.value ? "selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`q${index + currentStep * PAGE_SIZE}`}
                            value={option.value}
                            checked={responses[index + currentStep * PAGE_SIZE] === option.value}
                            onChange={(e) => handleChange(e, index + currentStep * PAGE_SIZE)}
                            required
                          />
                          <span className="option-text">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="navigation-controls">
                {currentStep > 0 && (
                  <button type="button" className="nav-button prev" onClick={handlePrev}>
                    <i className="bi bi-arrow-left"></i> Anterior
                  </button>
                )}
                {currentStep < steps - 1 ? (
                  <button type="button" className="nav-button next" onClick={handleNext}>
                    Siguiente <i className="bi bi-arrow-right"></i>
                  </button>
                ) : (
                  <button type="submit" className="nav-button submit">
                    Finalizar <i className="bi bi-check-circle"></i>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default HardSkills;
